import React from "react";
import {Flex, Grid, IconButton, Text, Tooltip} from "@chakra-ui/react";
import {FiArrowDown, FiArrowUp, FiTrash2} from "react-icons/fi";
import {BsSoundwave} from "react-icons/bs";
import {Zone} from "../../components/types";
import {CUE_LIST_GRID_TEMPLATE} from "./cueListLayout";
import {formatMillisecondsAsTimeCode} from "./cueListHelpers";

const ICON_SLOT_SIZE_PX = 18;

type Props = {
    zone: Zone;
    canEdit: boolean;
    isSelected: boolean;
    isDropTarget?: boolean;
    isReorderTarget?: boolean;
    reorderMarkerPosition?: "top" | "bottom";
    isDragging?: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onSelect: (zone: Zone, event: React.MouseEvent<HTMLDivElement>) => void;
    onDelete: (zone: Zone) => void;
    onMoveUp: (zone: Zone) => void;
    onMoveDown: (zone: Zone) => void;
    onRowDragStart?: (event: React.DragEvent<HTMLDivElement>, zone: Zone) => void;
    onRowDragEnd?: (event: React.DragEvent<HTMLDivElement>, zone: Zone) => void;
    onRowDragOver?: (event: React.DragEvent<HTMLDivElement>, zone: Zone) => void;
    onRowDragLeave?: (event: React.DragEvent<HTMLDivElement>, zone: Zone) => void;
    onRowDrop?: (event: React.DragEvent<HTMLDivElement>, zone: Zone) => void;
    showActions?: boolean;
    uploadingFileName?: string;
};

const CueListAmbientRow = ({
                               zone,
                               canEdit,
                               isSelected,
                               isDropTarget,
                               isReorderTarget,
                               reorderMarkerPosition,
                               isDragging,
                               canMoveUp,
                               canMoveDown,
                               onSelect,
                               onDelete,
                               onMoveUp,
                               onMoveDown,
                               onRowDragStart,
                               onRowDragEnd,
                               onRowDragOver,
                               onRowDragLeave,
                               onRowDrop,
                               showActions = true,
                               uploadingFileName
                           }: Props) => {
    const soundFile = zone.soundFiles?.ambient;
    const soundFileName = soundFile?.originalFileName || soundFile?.fileName || "";
    const isUploading = !!uploadingFileName;

    return (
        <Grid
            gridTemplateColumns={CUE_LIST_GRID_TEMPLATE}
            alignItems={"center"}
            px={3}
            py={2}
            borderBottomWidth={"1px"}
            borderColor={isReorderTarget ? "transparent" : "purple.100"}
            cursor={canEdit ? "grab" : "pointer"}
            draggable={canEdit}
            bg={isDropTarget ? "teal.50" : "purple.50"}
            _hover={{bg: isSelected ? "purple.100" : "purple.100"}}
            boxShadow={isDropTarget
                ? "inset 0 0 0 1px var(--chakra-colors-teal-300)"
                : isReorderTarget
                    ? (reorderMarkerPosition === "bottom"
                        ? "inset 0 -4px 0 var(--chakra-colors-orange-500)"
                        : "inset 0 4px 0 var(--chakra-colors-orange-500)")
                    : undefined}
            opacity={isDragging ? 0.7 : 1}
            onDragStart={(event) => {
                event.stopPropagation();
                onRowDragStart && onRowDragStart(event, zone);
            }}
            onDragEnd={(event) => {
                event.stopPropagation();
                onRowDragEnd && onRowDragEnd(event, zone);
            }}
            onDragOver={(event) => {
                event.stopPropagation();
                onRowDragOver && onRowDragOver(event, zone);
            }}
            onDragLeave={(event) => {
                event.stopPropagation();
                onRowDragLeave && onRowDragLeave(event, zone);
            }}
            onDrop={(event) => {
                event.stopPropagation();
                onRowDrop && onRowDrop(event, zone);
            }}
            onClick={(event) => onSelect(zone, event)}
        >
            <Text fontSize={"sm"} color={"purple.400"} fontWeight={"semibold"}>
                AMB
            </Text>

            <Text fontSize={"sm"} noOfLines={1} fontWeight={isSelected ? "semibold" : "medium"} color={"purple.700"}>
                {zone.label || zone.name || "Ambient"}
            </Text>

            <Flex align={"center"} gap={1} minW={0}>
                <Flex width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} align={"center"} justify={"center"} flexShrink={0}>
                    {isUploading || soundFileName ? <BsSoundwave size={14} color={"var(--chakra-colors-purple-500)"}/> : null}
                </Flex>
                <Text
                    fontSize={"sm"}
                    color={isUploading ? "orange.500" : (soundFileName ? "purple.600" : "gray.400")}
                    fontStyle={isUploading ? "italic" : undefined}
                    noOfLines={1}
                >
                    {isUploading ? `${uploadingFileName} uploading…` : (soundFileName || "No sound")}
                </Text>
            </Flex>

            <Text fontSize={"xs"} color={soundFile?.delay ? "purple.600" : "gray.400"}>
                {soundFile?.delay ? formatMillisecondsAsTimeCode(soundFile.delay) : "--"}
            </Text>

            <Text fontSize={"xs"} color={soundFile?.fadeIn ? "purple.600" : "gray.400"}>
                {soundFile?.fadeIn ? formatMillisecondsAsTimeCode(soundFile.fadeIn) : "--"}
            </Text>

            <Text fontSize={"xs"} color={soundFile?.fadeOut ? "purple.600" : "gray.400"}>
                {soundFile?.fadeOut ? formatMillisecondsAsTimeCode(soundFile.fadeOut) : "--"}
            </Text>

            <Text fontSize={"xs"} color={"purple.400"} fontStyle={"italic"} noOfLines={1}>
                ambient start
            </Text>

            <Flex justifySelf={"center"} align={"center"} gap={1} minH={"24px"}>
                <Flex width={"24px"} height={"24px"} align={"center"} justify={"center"}/>
                <Flex width={`${ICON_SLOT_SIZE_PX}px`} height={`${ICON_SLOT_SIZE_PX}px`} align={"center"} justify={"center"}/>
            </Flex>

            <Flex
                gap={1}
                justifySelf={"end"}
                align={"center"}
                minH={"24px"}
                onClick={(event) => event.stopPropagation()}
            >
                {showActions ? (
                    <>
                        <Tooltip label={"Move ambient cue up"} hasArrow>
                            <IconButton
                                aria-label={"Move ambient cue up"}
                                icon={<FiArrowUp/>}
                                size={"xs"}
                                variant={"ghost"}
                                onClick={() => onMoveUp(zone)}
                                isDisabled={!canEdit || !canMoveUp}
                            />
                        </Tooltip>
                        <Tooltip label={"Move ambient cue down"} hasArrow>
                            <IconButton
                                aria-label={"Move ambient cue down"}
                                icon={<FiArrowDown/>}
                                size={"xs"}
                                variant={"ghost"}
                                onClick={() => onMoveDown(zone)}
                                isDisabled={!canEdit || !canMoveDown}
                            />
                        </Tooltip>
                        <Tooltip label={"Delete ambient cue"} hasArrow>
                            <IconButton
                                aria-label={"Delete ambient cue"}
                                icon={<FiTrash2/>}
                                size={"xs"}
                                variant={"ghost"}
                                colorScheme={"red"}
                                onClick={() => onDelete(zone)}
                                isDisabled={!canEdit}
                            />
                        </Tooltip>
                    </>
                ) : <Flex minH={"24px"} minW={"24px"}/>}
            </Flex>
        </Grid>
    );
};

export default CueListAmbientRow;
